"use client";

import { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { X, ChevronDown, Loader2 } from "lucide-react";
import Image from "next/image";
import { useAuth } from "@/modules/auth/context/AuthContext";
import RoleSwitcher from "@/modules/auth/components/RoleSwitcher";
import { usePublicContext } from "@/modules/public/home/context/PublicContext";

interface NavSubcategory {
  name: string;
  slug: string;
  subsets?: Array<{ name: string; slug: string }>;
}

interface NavCategory {
  _id: string;
  name: string;
  slug: string;
  subcategories: NavSubcategory[];
}

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const STATIC_LINKS = [
  { label: "Israel-Agritech", href: "/israel-agritech" },
  { label: "FPO Connect", href: "/fpo-connect" },
  { label: "Vendor Profile", href: "/vendor-profile" },
  { label: "About Us", href: "/about" },
  { label: "Contact Us", href: "/contact" },
];

export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { categories: rawCategories, subcategories: rawSubcategories, loadingCategories, loadingSubcategories } = usePublicContext();
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const [subDropdown, setSubDropdown] = useState<string | null>(null);

  const { user, logout } = useAuth();

  const isPublicUser = user && (user.role === "buyer" || user.role === "fpo_connect");
  const publicUser = isPublicUser ? user : null;

  const loading = loadingCategories || loadingSubcategories;

  // Derive the navigation tree from raw data instead of effect+setState
  const categories = useMemo<NavCategory[]>(() => {
    if (rawCategories.length === 0) return [];

    return rawCategories.map((cat) => {
      const catSubs = rawSubcategories
        .filter((sub) => {
          const subCatId = typeof sub.category === "object" && sub.category !== null
            ? (sub.category as any)._id
            : sub.category;
          return subCatId === cat._id;
        })
        .map((sub) => ({
          name: sub.name,
          slug: sub.slug,
          subsets: [],
        }));

      return {
        _id: cat._id,
        name: cat.name,
        slug: cat.slug,
        subcategories: catSubs,
      };
    });
  }, [rawCategories, rawSubcategories]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const toggleDropdown = (key: string) =>
    setMobileDropdown((v) => (v === key ? null : key));

  const handleClose = () => {
    setMobileDropdown(null);
    setSubDropdown(null);
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 z-[100] lg:hidden transition-all duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-zinc-900/60 backdrop-blur-xs transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
          }`}
        onClick={handleClose}
      />

      {/* Sidebar */}
      <div
        className={`absolute top-0 left-0 h-full w-[85vw] max-w-sm bg-white shadow-2xl flex flex-col overflow-hidden transition-transform duration-300 ease-out transform ${isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-5 bg-[#005a2b] text-white border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 relative bg-white rounded-full border-2 border-white/30 shadow-md overflow-hidden shrink-0">
              <Image
                src="/KAB.webp"
                alt="Karnataka AgriBusiness Logo"
                fill
                className="object-contain p-1"
                unoptimized
              />
            </div>
            <span className="text-base font-black tracking-tight">
             KarnatakaAgribusiness
            </span>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/10 active:bg-white/20 rounded-lg transition-colors cursor-pointer"
            aria-label="Close Menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* User Status / Auth Card */}
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50 shrink-0">
          {publicUser ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-800 text-white flex items-center justify-center font-black text-sm shadow-sm shrink-0 uppercase">
                {publicUser.name ? publicUser.name.charAt(0) : "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Logged In As</p>
                <h4 className="text-sm font-black text-slate-800 truncate leading-tight">{publicUser.name}</h4>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${publicUser.role === 'fpo_connect'
                      ? 'bg-amber-100 text-amber-800 border border-amber-200'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                    }`}>
                    {publicUser.role === 'fpo_connect' ? 'FPO Portal' : 'Buyer'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center shrink-0">
                  <span className="text-xs font-black">?</span>
                </div>
                <div>
                  <h4 className="text-[11px] font-black text-slate-700 uppercase tracking-wider leading-none">Welcome Guest</h4>
                  <p className="text-[10px] text-slate-400 font-bold mt-0.5">Access your account or register today</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Link
                  href="/auth/login"
                  className="flex items-center justify-center py-2 px-3 text-xs font-black text-white bg-[#005a2b] rounded-lg hover:bg-green-800 transition-colors shadow-sm text-center uppercase tracking-wider cursor-pointer"
                  onClick={handleClose}
                >
                  Login
                </Link>
                <Link
                  href="/auth/register"
                  className="flex items-center justify-center py-2 px-3 text-xs font-black text-[#005a2b] bg-white border border-green-200 rounded-lg hover:bg-green-50 transition-colors shadow-sm text-center uppercase tracking-wider cursor-pointer"
                  onClick={handleClose}
                >
                  Register
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto pt-4 pb-8 px-4 font-sans">
          {loading ? (
            <div className="p-8 text-center flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 text-green-600 animate-spin mb-2" />
              <span className="text-xs text-slate-500 font-bold">Synchronizing catalog...</span>
            </div>
          ) : (
            <ul className="space-y-2">
              <li>
                <Link
                  href="/"
                  className="flex items-center px-4 py-3 text-sm font-bold text-zinc-700 bg-zinc-50 rounded-xl hover:bg-green-50 hover:text-green-800 transition-all border border-zinc-100"
                  onClick={handleClose}
                >
                  Home
                </Link>
              </li>

              {/* Product Categories */}
              {categories.map((cat) => (
                <li key={cat._id} className="space-y-1">
                  <div
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${mobileDropdown === cat._id
                        ? "bg-green-50 border-green-200"
                        : "bg-white border-zinc-100"
                      }`}
                  >
                    <Link
                      href={`/${cat.slug}`}
                      className={`text-sm font-bold flex-1 ${mobileDropdown === cat._id ? "text-green-800" : "text-zinc-700"
                        }`}
                      onClick={handleClose}
                    >
                      {cat.name}
                    </Link>
                    {cat.subcategories.length > 0 && (
                      <button
                        onClick={() => toggleDropdown(cat._id)}
                        className="p-2 -mr-2 text-zinc-400 hover:text-green-800 transition-colors cursor-pointer"
                        aria-label={`Toggle ${cat.name} subcategories`}
                      >
                        <ChevronDown
                          className={`w-5 h-5 transition-transform duration-200 ${mobileDropdown === cat._id ? "rotate-180" : ""
                            }`}
                        />
                      </button>
                    )}
                  </div>

                  {mobileDropdown === cat._id && (
                    <div className="pl-4 pr-2 space-y-1 mt-1 transition-all">
                      {cat.subcategories.map((sub) => (
                        <div key={sub.slug}>
                          <div className="flex items-center justify-between p-2.5 rounded-lg hover:bg-zinc-50">
                            <Link
                              href={`/${cat.slug}/${sub.slug}`}
                              className="text-sm font-bold text-zinc-600"
                              onClick={handleClose}
                            >
                              {sub.name}
                            </Link>
                            {(sub.subsets?.length ?? 0) > 0 && (
                              <button
                                onClick={() =>
                                  setSubDropdown(
                                    subDropdown === sub.slug ? null : sub.slug
                                  )
                                }
                                className="p-1.5 text-zinc-400 hover:text-green-800 cursor-pointer"
                                aria-label={`Toggle ${sub.name} subsets`}
                              >
                                <ChevronDown
                                  className={`w-4 h-4 transition-transform duration-200 ${subDropdown === sub.slug ? "rotate-180" : ""
                                    }`}
                                />
                              </button>
                            )}
                          </div>
                          {subDropdown === sub.slug && (
                            <div className="pl-6 space-y-1">
                              {sub.subsets?.map((subset) => (
                                <Link
                                  key={subset.slug}
                                  href={`/${cat.slug}/${sub.slug}/${subset.slug}`}
                                  className="block p-2 text-[13px] font-bold text-zinc-500 hover:text-green-700"
                                  onClick={handleClose}
                                >
                                  {subset.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}

              {/* Static Links */}
              {STATIC_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="flex items-center px-4 py-3 text-sm font-bold text-zinc-700 bg-white rounded-xl border border-zinc-100 hover:bg-green-50 hover:border-green-200 transition-all"
                    onClick={handleClose}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}

              {/* User-specific Portal Links (FPO only on public menu) */}
              {publicUser && publicUser.role === 'fpo_connect' && (
                <li className="px-4 py-2 border border-zinc-200 rounded-xl bg-slate-50 flex items-center justify-between">
                  <span className="text-xs font-bold text-zinc-600">Switch Portal</span>
                  <RoleSwitcher />
                </li>
              )}

              {/* Logout Button if Logged In */}
              {publicUser && (
                <li className="pt-4 border-t border-slate-100">
                  <button
                    onClick={() => {
                      logout();
                      handleClose();
                    }}
                    className="flex items-center justify-center w-full px-4 py-3 text-xs font-black text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 rounded-xl transition-all uppercase tracking-wider cursor-pointer"
                  >
                    Logout Account
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
